import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthSession } from '@aws-amplify/auth';
import {
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Link,
  Chip,
  Tooltip,
} from '@mui/material';
import { generatePatient, incrementAdmissionCount, setDatasetType, setTrialProtocol } from '../redux/userSlice';
import { track } from '@amplitude/analytics-browser';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateAdmissionsMutation,
  useGetProceduresQuery,
  useGenerateTrialSubjectsMutation,
} from '../redux/medchartApi';
import { useDebounceValue } from '../hooks/useDebounceValue';
import UsageBanner from './UsageBanner';
import WelcomeCard from './WelcomeCard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import featureFlags from '../utils/featureFlags';
import { startAdmissionStream, mapAndPersistAdmissionUuid } from '../redux/streamingSlice';
import AdmissionGenerationProgress from '../components/AdmissionGenerationProgress';
import config from '../utils/Constants';
import DatasetTypeToggle from '../components/DatasetTypeToggle';
import TrialProtocolConfig from '../components/TrialProtocolConfig';
import TrialSubjectsCatalog from './TrialSubjectsCatalog';

const GeneratePatients = () => {
    const [textFieldValue, setTextFieldValue] = useState('');
    const [autoCompleteValue, setAutoCompleteValue] = useState('');
    const medchartValue = useDebounceValue(textFieldValue, 1000);
    const procedures = useGetProceduresQuery(
      { query: medchartValue },
      { skip: !medchartValue }
    );
    const [createAdmissions] = useCreateAdmissionsMutation();
    const [generateTrialSubjects] = useGenerateTrialSubjectsMutation();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [patientCount, setPatientCount] = useState(1);
    const [isFirstPatientGenerated, setIsFirstPatientGenerated] = useState(false);
    const [patientData, setPatientData] = useState([]);
    const [additionalPatientData, setAdditionalPatientData] = useState([]);
    const [streamingUuids, setStreamingUuids] = useState([]);

    // Trial subjects state
    const [trialSubjects, setTrialSubjects] = useState([]);
    const [activeProtocol, setActiveProtocol] = useState(null);

    const selectedTenant = useSelector((state) => state.user.selectedTenant);
    const datasetType = useSelector((state) => state.user.datasetType);
    const trialProtocol = useSelector((state) => state.user.trialProtocol);
    const { tenantId: tenantIdParam } = useParams();
    const tenantId = tenantIdParam || selectedTenant?.id;

    const admissionLimit = selectedTenant?.admission_limit ?? 20;
    const admissionCount = selectedTenant?.admission_count ?? 0;
    const remaining = Math.max(0, admissionLimit - admissionCount);
    const isAtLimit = admissionCount >= admissionLimit;
    const userEmail = useSelector((state) => state.user.email || '');

    if (!selectedTenant) {
        return <Alert severity="warning">Please select a tenant to generate patients.</Alert>;
    }

    const handleCreateAdmissions = async () => {
      try {
        const result = await createAdmissions({
          icdCode: autoCompleteValue.icd_code,
          icdVersion: autoCompleteValue.icd_version,
        }).unwrap();

        track('Create admissions');
        return result;
      } catch (e) {
        track('Error in creating admissions');
        return null;
      }
    };

    // Creates a CyntheticAdmission on the streaming backend and returns its UUID.
    const createLocalCyntheticAdmission = async (patientResponse) => {
      const apiBase = selectedTenant?.api_base_url || config.getBaseUrl();
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) return null;
        const token = session.tokens.idToken.toString();
        const res = await fetch(`${apiBase}/api/v1/medchart/cynthetic-admissions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            age: patientResponse?.age ?? null,
            gender: patientResponse?.gender ?? null,
            input_icd_code: autoCompleteValue?.icd_code ?? null,
            admission_uuid: patientResponse?.admission_uuid ?? null,
          }),
        });
        if (!res.ok) return null;
        const ca = await res.json();
        return ca.uuid;
      } catch {
        return null;
      }
    };

   const handleGenerateSinglePatient = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dispatch(generatePatient()).unwrap(); // Unwrap response for direct data access
            track('Generate Patient');
            dispatch(incrementAdmissionCount(1));
            setPatientData([response]); // Store first patient's data in the first table
            setIsFirstPatientGenerated(true);

            if (autoCompleteValue?.icd_code) {
              handleCreateAdmissions(); // fire-and-forget old workflow
              if (featureFlags.STREAMING_GENERATION) {
                const uuid = await createLocalCyntheticAdmission(response);
                if (uuid) {
                  if (response?.admission_uuid) {
                    dispatch(mapAndPersistAdmissionUuid({ mainUuid: response.admission_uuid, streamingUuid: uuid }));
                  }
                  setStreamingUuids((prev) => [...prev, uuid]);
                  dispatch(startAdmissionStream(uuid));
                }
              }
            }
        } catch (error) {
            const status = error?.status;
            const detailMessage = error?.data?.detail || error?.message;
            if (status === 400 && detailMessage?.includes('admission_limit')) {
                setError(
                  'You have reached the limit for generating new patients.'
                )
        } else {
            setError(error.message)
        } 
    }
        finally {
            setLoading(false);
        }
    };

    const handleGenerateMultiplePatients = async () => {
        setLoading(true);
        setError(null);
        try {
            const responses = [];
            for (let i = 0; i < patientCount; i++) {
                const response = await dispatch(generatePatient()).unwrap();
                responses.push(response);
            }
            setAdditionalPatientData(responses); // Store additional patients after the first in a separate table
            dispatch(incrementAdmissionCount(responses.length));
            track('Generate Patients', { count: patientCount });

            if (autoCompleteValue?.icd_code) {
              handleCreateAdmissions(); // fire-and-forget old workflow
              if (featureFlags.STREAMING_GENERATION) {
                const uuid = await createLocalCyntheticAdmission(responses[0]);
                if (uuid) {
                  if (responses[0]?.admission_uuid) {
                    dispatch(mapAndPersistAdmissionUuid({ mainUuid: responses[0].admission_uuid, streamingUuid: uuid }));
                  }
                  setStreamingUuids((prev) => [...prev, uuid]);
                  dispatch(startAdmissionStream(uuid));
                }
              }
            }
        } catch (error) {
            const status = error?.status;
            const detailMessage = error?.data?.detail || error?.message;

            if (status === 400 && detailMessage?.includes('admission_limit')) {
                setError(
                  'You have reached the limit for generating new patients.'
                );
            } else {
            setError(error.message);
        }}
        finally {
            setLoading(false);
        }
    };

    const handleSliderChange = (event, value) => {
        setPatientCount(value);
    };

    const handleViewAdmissions = () => {
        navigate(tenantId ? `/dashboard/${tenantId}` : '/dashboard');
    };

    const handleGenerateTrialSubjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await generateTrialSubjects({
                protocol: trialProtocol,
                count: patientCount,
            }).unwrap();
            setTrialSubjects(result);
            // Build a lightweight protocol object for the catalog banner
            setActiveProtocol({
                id: result[0]?.protocol_id ?? null,
                therapeutic_area: trialProtocol.therapeutic_area,
                phase: trialProtocol.phase,
                arms: trialProtocol.arms,
            });
            track('Generate Trial Subjects', { count: patientCount, therapeutic_area: trialProtocol.therapeutic_area });
        } catch (err) {
            setError(err?.data?.detail || err?.message || 'Failed to generate trial subjects');
        } finally {
            setLoading(false);
        }
    };

    const handleExportTrialSubjects = async () => {
        if (!activeProtocol?.id) return;
        try {
            const { fetchAuthSession } = await import('@aws-amplify/auth');
            const session = await fetchAuthSession();
            const token = session.tokens.idToken.toString();
            const baseUrl = config.getDynamicApiUrl();
            const resp = await fetch(
                `${baseUrl}/api/v1/client/trial-subjects/export/?protocol_id=${activeProtocol.id}&format=sdtm_csv`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!resp.ok) throw new Error('Export failed');
            const blob = await resp.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sdtm-export-protocol-${activeProtocol.id}.zip`;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 100);
            track('Export Trial Subjects SDTM', { protocol_id: activeProtocol.id });
        } catch (err) {
            setError('Export failed: ' + (err?.message || 'unknown error'));
        }
    };

    const handleTextFieldChange = (event) => {
      setTextFieldValue(event.target.value);
    };

    const handleAutoCompleteChange = (_event, value) => {
      setAutoCompleteValue(value);
    };

    const autoCompleteOptions = procedures.data?.items.map((item) => {
      return { label: item.long_title, ...item };
    });

    return (
        <>
            <Typography variant="h5" gutterBottom>
              {datasetType === 'trial' ? 'Generate Clinical Trial Data' : 'Generate Patients'}
              {featureFlags.STREAMING_GENERATION && datasetType !== 'trial' && (
                <Tooltip title="AI generation streams section-by-section after patient creation. Select an ICD code below to activate.">
                  <Chip
                    icon={<AutoAwesomeIcon />}
                    label="AI Streaming Active"
                    color="primary"
                    size="small"
                    sx={{ ml: 1.5, verticalAlign: 'middle' }}
                  />
                </Tooltip>
              )}
            </Typography>
            <UsageBanner used={admissionCount} limit={admissionLimit} />
            <WelcomeCard />

            {/* Dataset type toggle */}
            <DatasetTypeToggle
                value={datasetType}
                onChange={(type) => {
                    dispatch(setDatasetType(type));
                    if (type === 'admission') {
                        dispatch(setTrialProtocol(null)); // reset stale protocol state on mode switch
                    }
                    setError(null);
                    setTrialSubjects([]);
                    setActiveProtocol(null);
                }}
            />

            {/* ---- TRIAL MODE ---- */}
            {datasetType === 'trial' && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Configure a study protocol below, then generate synthetic clinical trial subjects.
                        Output includes DM, AE, LB, VS, EX, and DS SDTM domains.
                    </Alert>
                    <TrialProtocolConfig
                        protocol={trialProtocol}
                        onChange={(updated) => dispatch(setTrialProtocol(updated))}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                        Subjects to generate
                    </Typography>
                    <Slider
                        value={patientCount}
                        min={1}
                        max={100}
                        step={1}
                        marks={[1, 10, 25, 50, 100].map((v) => ({ value: v, label: String(v) }))}
                        onChange={handleSliderChange}
                        valueLabelDisplay="auto"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleGenerateTrialSubjects}
                        disabled={loading}
                        sx={{ mt: 1 }}
                    >
                        {loading ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Generating...</> : `Generate ${patientCount} Subject${patientCount > 1 ? 's' : ''}`}
                    </Button>

                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                    {trialSubjects.length > 0 && activeProtocol && (
                        <>
                            <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
                                {trialSubjects.length} synthetic subject{trialSubjects.length > 1 ? 's' : ''} generated across {trialProtocol.arms.length} arms.
                                Export as SDTM ZIP to use in SAS, R, or your EDC system.
                            </Alert>
                            <TrialSubjectsCatalog
                                subjects={trialSubjects}
                                protocol={activeProtocol}
                                onExport={handleExportTrialSubjects}
                            />
                        </>
                    )}
                </>
            )}

            {/* ---- ADMISSION MODE ---- */}
            {datasetType === 'admission' && (<>
            {isAtLimit && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    You have used all {admissionLimit} free records. Upgrade to generate more.
                </Alert>
            )}
            <Autocomplete
              disablePortal
              filterOptions={(x) => x}
              loading={procedures.isFetching}
              onChange={handleAutoCompleteChange}
              options={autoCompleteOptions || []}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    label="Search for a Procedure or ICD_10 Code to base a synthetic generation on"
                    onChange={handleTextFieldChange}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {procedures.isFetching && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />

            {admissionCount <= 0 ? (
                <>
                    <Typography variant="body1" gutterBottom>
                        Click the button below to generate a single patient and start populating your data.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleGenerateSinglePatient}
                        disabled={loading || isAtLimit}
                    >
                        {loading ? 'Generating...' : 'Generate Patient'}
                    </Button>
                    <TableContainer component={Paper} sx={{ mt: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Patient ID</TableCell>
                                    <TableCell>Full Name</TableCell>
                                    <TableCell>Age</TableCell>
                                    <TableCell>Gender</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Date of Birth</TableCell>
                                    <TableCell>Ethnicity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {patientData.map((patient, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{patient.id}</TableCell>
                                        <TableCell>{`${patient.firstname} ${patient.middlename} ${patient.lastname}`}</TableCell>
                                        <TableCell>{patient.age}</TableCell>
                                        <TableCell>{patient.gender}</TableCell>
                                        <TableCell>{`${patient.street_address}, ${patient.city}, ${patient.state} ${patient.zip}`}</TableCell>
                                        <TableCell>{patient.dob}</TableCell>
                                        <TableCell>{patient.ethnicity}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {isFirstPatientGenerated && (
                    <Alert severity="success" gutterBottom sx={{ mt: 2 }}>
                        {`Great! You generated your first patient. We automatically create admissions health data for every patient that you generate. Use the slider to generate up to ${admissionLimit} patients, for free. Generating more patients requires an upgrade.`}

                    </Alert>
                    )}
                </>
            ) : (
                <>
                    <Alert severity="info" sx={{ mt: 4 }}> 
                    Use the slider to generate up to {admissionLimit} patients, for free. Generating more patients requires an upgrade. We automatically create admissions health data for every patient that you generate.</Alert>
                    
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Records to generate ({remaining} remaining free)
                    </Typography>
                    <Slider
                        value={patientCount}
                        min={1}
                        max={Math.max(1, remaining)}
                        step={1}
                        marks
                        onChange={handleSliderChange}
                        valueLabelDisplay="auto"
                        disabled={isAtLimit}
                        sx={{ mt: 1 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleGenerateMultiplePatients}
                        disabled={loading || isAtLimit || patientCount > remaining}
                        sx={{ mt: 2 }}
                    >
                        {loading ? 'Generating...' : `Generate ${patientCount} Patient(s)`}
                    </Button>

                    {admissionCount > admissionLimit && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            <stripe-buy-button
                            buy-button-id="buy_btn_1QXmbIAnYtC91dgVo0R4PJsx"
                            publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                            customer-email= {userEmail}
                            style ={{marginRight:"10px"}}
                        ></stripe-buy-button>
                            <stripe-buy-button
                            buy-button-id="buy_btn_1QXV0jAnYtC91dgVcRjtr5JB"
                            publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                            customer-email= {userEmail}
                            style ={{marginRight:"10px"}}
                        ></stripe-buy-button>
                        <stripe-buy-button
                            buy-button-id="buy_btn_1QXmcGAnYtC91dgV5C0kTRvb"
                            publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                            customer-email= {userEmail}
                        >
                        </stripe-buy-button>
                        </Typography>
                    )}

                    {/* Render second table for additional generated patients */}
                    {additionalPatientData.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ mt: 4 }}>Generated Patients</Typography>
                            <Alert severity="success" gutterBottom sx={{ mt: 2 }}>
                                {`Great! You generated some patients. We automatically create admissions health data for every patient that you generate. 
                                View the admissions for each patient below or create some more patients!`}

                            </Alert>
                            <TableContainer component={Paper} sx={{ mt: 3 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Patient ID</TableCell>
                                            <TableCell>Full Name</TableCell>
                                            <TableCell>Age</TableCell>
                                            <TableCell>Gender</TableCell>
                                            <TableCell>Address</TableCell>
                                            <TableCell>Date of Birth</TableCell>
                                            <TableCell>Ethnicity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {additionalPatientData.map((patient, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{patient.id}</TableCell>
                                                <TableCell>{`${patient.firstname} ${patient.middlename} ${patient.lastname}`}</TableCell>
                                                <TableCell>{patient.age}</TableCell>
                                                <TableCell>{patient.gender}</TableCell>
                                                <TableCell>{`${patient.street_address}, ${patient.city}, ${patient.state} ${patient.zip}`}</TableCell>
                                                <TableCell>{patient.dob}</TableCell>
                                                <TableCell>{patient.ethnicity}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}

                    <Button
                        variant="outlined"
                        onClick={handleViewAdmissions}
                        sx={{ mt: 2 }}
                    >
                        View Admissions
                    </Button>
                </>
            )}

            {featureFlags.STREAMING_GENERATION && datasetType !== 'trial' && streamingUuids.map((uuid) => (
              <AdmissionGenerationProgress
                key={uuid}
                admissionUuid={uuid}
                onDismiss={() => setStreamingUuids((prev) => prev.filter((u) => u !== uuid))}
              />
            ))}

            {featureFlags.STREAMING_GENERATION && datasetType !== 'trial' && process.env.NODE_ENV === 'development' && (
              <Button
                variant="text"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}
                onClick={() => {
                  const testUuid = 'b37e44c6-9cfa-49fe-8ad5-f6f3f11459a0';
                  setStreamingUuids((prev) => prev.includes(testUuid) ? prev : [...prev, testUuid]);
                  dispatch(startAdmissionStream(testUuid));
                }}
              >
                [dev] Test stream
              </Button>
            )}

            {error && datasetType === 'admission' && (
            <>
            <Alert severity="warning" style={{marginTop:'10px'}}>
                {error}
                <Typography
                // href="https://buy.stripe.com/00g7uZ4hvb1P2NG8wz"
                // href="https://buy.stripe.com/test_dR6g0efBkdrhdFK3cc" // Test link
                style={{ marginLeft: '5px', marginBottom: '1rem' }}
                >
                    Click below to purchase additional credits. 1 credit = 1 patient + 1 admission.
                </Typography>
                <stripe-buy-button
                buy-button-id="buy_btn_1QXmbIAnYtC91dgVo0R4PJsx"
                publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                customer-email= {userEmail}
                style ={{marginRight:"10px"}}
              ></stripe-buy-button>
                <stripe-buy-button
                buy-button-id="buy_btn_1QXV0jAnYtC91dgVcRjtr5JB"
                publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                customer-email= {userEmail}
                style ={{marginRight:"10px"}}
              ></stripe-buy-button>
              <stripe-buy-button
                buy-button-id="buy_btn_1QXmcGAnYtC91dgV5C0kTRvb"
                publishable-key="pk_live_51Ow9hmAnYtC91dgVseC5xftenSu6YvQCYCzWkukWwiZhzBJweJwA4UsRaLRB7IxDtPlHPvDlpISNnc7sIwe8rmd200RUH2J7NT"
                customer-email= {userEmail}
              >
              </stripe-buy-button>
              <Link 
                href="mailto:sales@bacclabs.io" 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ display: "block" }}
                >
                Need more? Send us an email
                </Link>            
            </Alert>
            </>
            )
            }
            </>)}
        </>
    );
};

export default GeneratePatients;
